import { Alert } from '@api';
import { Button } from '@design/components/button/Button';
import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import { DropdownGroup } from '@design/components/dropdown-menu/dropdown-group/DropdownGroup';
import { useInsightDrawer } from '@terminal/hooks/use-insight-drawer/useInsightDrawer';
import { useConversationTask } from '@terminal/react-query-hooks/alfred/useConversationTask';
import { usePostRead } from '@terminal/react-query-hooks/events/use-event-alerts/usePostRead';
import { usePostSubscription } from '@terminal/react-query-hooks/events/use-event-alerts/usePostSubscription';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useChatNavigation } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './AlertsCard.module.scss';
import { AlertsAlfredCard } from './components/alerts-alfred-card/AlertsAlfredCard';
import { AlertsInsightCard } from './components/alerts-insight-card/AlertsInsightCard';

export interface AlertsCardProps {
  alertEvent: Alert;
  onNavigate?: () => void;
  trackingSource?: PosthogTrackingSource;
  compact?: boolean;
}

export const AlertsCard = ({
  alertEvent,
  onNavigate,
  trackingSource = POSTHOG_TRACKING_SOURCES.ALERTS,
  compact = false,
}: AlertsCardProps) => {
  const { t } = useTranslation('alerts', { useSuspense: false });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { mutate: markAsRead } = usePostRead();
  const { mutate: toggleTracking } = usePostSubscription();
  const { openDrawer } = useInsightDrawer();
  const { navigateToConversation, setAlfredTrackingSource } =
    useChatNavigation();
  const { trackAlertTracked, trackAlertUntracked, getAlertProperties } =
    usePosthogEvents();
  const {
    read,
    source_type: sourceType,
    tracking_status: trackingStatus,
  } = alertEvent;
  const isTrackingActive = trackingStatus === 'active';
  const isAlfredCard = sourceType === 'alfred';
  const { data: alfredConversationData, isLoading: isAlfredConversationLoading } =
    useConversationTask(alertEvent.source_id, isAlfredCard);
  const alfredRequestId = alertEvent.metadata?.payload?.request_id;
  const alfredTask =
    alfredConversationData?.tasks?.find(
      task => task.request_id === alfredRequestId
    ) ?? alfredConversationData?.tasks?.[0];

  if (
    isAlfredCard &&
    !isAlfredConversationLoading &&
    !alfredTask?.last_state
  ) {
    return null;
  }

  const handleCardClick = () => {
    if (!read) {
      markAsRead([alertEvent.id]);
    }

    if (isAlfredCard) {
      setAlfredTrackingSource(trackingSource);
      navigateToConversation(alertEvent.source_id, {
        trackingSource,
      });
    } else {
      openDrawer({
        id: alertEvent.source_id,
        entityTag: alertEvent.metadata?.entity_tag,
        trackingProperties: {
          source: trackingSource,
          ...getAlertProperties(alertEvent),
        },
      });
    }

    onNavigate?.();
  };

  const dropdownItems = [
    [
      {
        label: read ? t('alerts:markAsUnread') : t('alerts:markAsRead'),
        key: 'read',
        onClick: () => markAsRead([alertEvent.id]),
      },
      ...(!isAlfredCard && trackingStatus && trackingStatus !== 'completed'
        ? [
            {
              label: isTrackingActive
                ? t('alerts:stopTracking')
                : t('alerts:resumeTracking'),
              key: 'tracking',
              onClick: () => {
                const trackFn = isTrackingActive
                  ? trackAlertUntracked
                  : trackAlertTracked;
                trackFn(trackingSource, alertEvent);
                toggleTracking({
                  source_id: alertEvent.source_id,
                  source_type: alertEvent.source_type,
                  track: !isTrackingActive,
                });
              },
            },
          ]
        : []),
    ],
  ];

  const dropdown = (
    <div onClick={e => e.stopPropagation()}>
      <DropdownMenu
        triggerNode={
          <Button variant="empty" size="xsmall" iconName="Ellipsis" />
        }
        align="end"
        onOpenChange={setIsDropdownOpen}
        contentProps={{
          style: {
            borderRadius: '8px',
            minWidth: 'unset',
            width: 'fit-content',
            backgroundColor: 'var(--background-soft)',
          },
        }}
      >
        {dropdownItems.map((group, key) => (
          <DropdownGroup items={group} key={key} />
        ))}
      </DropdownMenu>
    </div>
  );

  return (
    <div
      className={clsx(styles.root, {
        [styles.read]: read,
        [styles.isDropdownOpen]: isDropdownOpen,
      })}
      data-testid="alerts-card"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleCardClick();
        }
      }}
    >
      <div className={styles.contentWrapper}>
        {isAlfredCard ? (
          <AlertsAlfredCard
            alertEvent={alertEvent}
            conversationData={alfredConversationData}
            dropdown={dropdown}
            isLoading={isAlfredConversationLoading}
          />
        ) : (
          <AlertsInsightCard
            alertEvent={alertEvent}
            dropdown={dropdown}
            compact={compact}
          />
        )}
      </div>
    </div>
  );
};
